<template>
  <header>
    <!-- Navigation drawer -->
    <v-navigation-drawer
      v-if="userNavigationLinks.length"
      v-model="sidebar"
      fixed
      temporary
      @update:model-value="navStateChanged"
    >
      <v-list nav density="compact">
        <template v-for="(item, index) in userNavigationLinks">
          <v-list-group
            v-if="item.subMenus"
            :key="index"
            :data-cy="item.dataCy"
            :value="item.subMenus.find(sub => $route.path.startsWith(sub.route))?.tab"
          >
            <template #activator="{ props }">
              <v-list-item v-bind="props">
                <v-list-item-title>{{ item.tab }}</v-list-item-title>
              </v-list-item>
            </template>
            <v-list-item
              v-for="(submenuItem, i) in item.subMenus"
              :key="i"
              :to="submenuItem.route"
              :data-cy="submenuItem.dataCy"
              :active="$route.path.startsWith(submenuItem.route)"
            >
              <v-list-item-title>{{ submenuItem.tab }}</v-list-item-title>
            </v-list-item>
          </v-list-group>
          <v-list-item :key="item.route" v-else :to="item.route" :data-cy="item.dataCy">
            <v-list-item-title>{{ item.tab }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>
    <!-- App bar -->
    <v-app-bar :elevation="1" app :height="appBarHeight">
      <v-app-bar-nav-icon class="d-lg-none" @click="sidebar = true" v-if="userNavigationLinks.length" />
      <router-link :to="getHomePath()" class="logo-link">
        <img alt="Home" src="@/assets/images/cast-logo-white-green.svg" width="120" height="22" />
        <v-icon v-if="isEduMode || isEducational" class="logo-badge logo-badge--edu">mdi-school</v-icon>
        <span v-else-if="!isEduMode && isAdmin" class="logo-badge">PRO</span>
      </router-link>
      <!-- Toolbar -->
      <v-toolbar-items class="d-none d-lg-flex align-self-center">
        <template v-for="(item, index) in userNavigationLinks">
          <v-menu offset-y v-if="item.subMenus" :key="index">
            <template v-slot:activator="{ props }">
              <v-btn
                variant="text"
                v-bind="props"
                :data-cy="item.dataCy"
                :class="{ 'v-btn--active': item.subMenus.some(sub => $route.path.startsWith(sub.route)) }"
              >
                {{ item.tab }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(submenuItem, i) in item.subMenus"
                :key="i"
                :to="submenuItem.route"
                :active="$route.path.startsWith(submenuItem.route)"
                :data-cy="submenuItem.dataCy"
              >
                <v-list-item-title class="text-uppercase">{{ submenuItem.tab }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn
            v-else
            variant="text"
            :key="item.route"
            :to="item.route"
            :data-cy="item.dataCy"
            :class="{ 'v-btn--active': $route.path.startsWith(item.route) }"
          >
            {{ item.tab }}
          </v-btn>
        </template>
      </v-toolbar-items>
      <v-spacer />
      <!-- Shopping cart -->
      <v-btn
        variant="flat"
        v-if="isPurchaseAvailableForOrganization && (userRoleName === 'owner' || userRoleName === 'guest')"
        icon
        @click="goToCart"
        data-cy="go-to-cart-button"
        density="default"
      >
        <v-badge
          :content="cartProductsCount"
          :model-value="cartProductsCount > 0"
          color="primary"
          overlap
          data-cy="items-in-cart-count"
        >
          <v-icon>mdi-cart</v-icon>
        </v-badge>
      </v-btn>
      <div class="d-flex flex-column" v-if="isAdmin">
        <v-switch
          :model-value="isEduMode"
          @update:model-value="switchEduMode"
          color="primary"
          hide-details
          density="compact"
          class="ma-0 mr-2 pa-0"
        ></v-switch>
        <div class="mb-0 pa-0 mt-n2 text-caption">{{ isEduMode ? 'EDU' : 'PRO' }}</div>
      </div>
      <!-- Menu -->
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn
            variant="flat"
            icon
            size="x-large"
            v-bind="props"
            class="mx-2"
            :class="{ 'mr-0': !isNotXs }"
            data-cy="toggle-profile-menu-top"
            density="comfortable"
          >
            <moa-avatar
              :img="user?.avatar"
              :full-name="getUserFullName(user)"
              :size="48"
              data-cy="header-user-avatar"
            />
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="profileLink in profileLinks"
            :key="profileLink.tab"
            :data-cy="profileLink.dataCy"
            link
            @click="handleProfileLink(profileLink)"
          >
            <v-list-item-title>{{ profileLink.tab }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <div v-if="user && isNotXs" class="text-caption d-grid">
        <div
          :title="getUserFullName(user)"
          class="text-uppercase font-weight-bold text-truncate"
          data-cy="header-user-name"
        >
          {{ getUserFullName(user) }}
        </div>
        <div :title="user.email" class="text-truncate" data-cy="user-email-top">{{ user.email }}</div>
      </div>
    </v-app-bar>
    <v-dialog v-model="dialogLegal" max-width="400">
      <v-card>
        <v-toolbar flat>
          <v-toolbar-title>Legal</v-toolbar-title>
          <v-spacer />
          <v-btn variant="flat" icon @click="dialogLegal = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="mb-5">
          <div class="mt-2">
            <a
              href="https://cast-soft.com/cast/files/End_User_Licence_Agreement.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              EULA
            </a>
          </div>
          <div class="mt-1"><a href="https://cast-soft.com/terms-of-use">Terms of use</a></div>
          <div class="mt-1"><a href="https://cast-soft.com/privacy-policy/">Privacy Policy</a></div>
          <div class="mt-1 mb-2">
            <a href="https://cast-soft.com/cast/files/Terms-Conditions.pdf" target="_blank" rel="noopener noreferrer">
              General Sales Terms & Conditions
            </a>
          </div>
          Copyright ©{{ thisYear }} CAST Software. All rights reserved.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="flat" color="primary" @click="dialogLegal = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </header>
  <!-- <p> </p>
  <p> </p>
  <p> </p>
  <p> </p>
  <p> </p>
  curOrder [{{ curOrder }}] -->
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import store from '@/store';
import { useDisplay } from 'vuetify';
import MoaAvatar from '@/components/shared/common/MoaAvatar.vue';
import { getUserFullName, isTestEnv, YEAR_FORMAT, getHomePath, routes, type MoaRouterConfig } from '@/utils';
import type { UserDto } from '@/castapi';

type NavigationLink = {
  tab: string;
  route?: string;
  dataCy?: string;
  hidden?: boolean;
  roles?: string[];
  subMenus?: NavigationLink[];
};
type ProfileLink = {
  tab: string;
  route?: string;
  dataCy: string;
};

const router = useRouter();
const sidebar = ref(false);
const dialogLegal = ref(false);
const thisYear = ref(YEAR_FORMAT);
const display = useDisplay();

const handleProfileLink = (profileLink: ProfileLink): void => {
  if (profileLink.tab === 'Legal') {
    dialogLegal.value = true;
    return;
  }
  if (profileLink.route) {
    router.push(profileLink.route);
  }
};
const navStateChanged = ($event: boolean): void => {
  return $event
    ? document.body.classList.add('disable-scrolling')
    : document.body.classList.remove('disable-scrolling');
};
//const availableDemos = computed<DemoDto[]>(() => store.getters['demo/availableDemos']);
// const demoSubmenus = computed<NavigationLink[]>(() =>
//   availableDemos.value.map((demo: DemoDto) => ({
//     tab: demo.name?.toUpperCase(),
//     route: `/demo/${demo.demoLinkName}`,
//     dataCy: `link-demo-${demo.name?.toLowerCase()}`,
//   })),
// );
const otherSubmenus = computed<NavigationLink[]>(() => {
  const submenus = [
    {
      tab: 'Pending Users',
      route: '/pending-users',
    },
    {
      tab: 'Email Templates',
      route: '/email-templates',
    },
    {
      tab: 'Maintenance Windows',
      route: '/maintenance-windows',
    },
  ];
  if (isTestEnv()) {
    submenus.push({
      tab: 'Dongle-Web Simulator',
      route: '/dongle-web-simulator',
    });
  }
  return submenus;
});
const phoneValidated = computed<boolean>(() => store.getters['login/phoneValidated']);
// const curOrder = computed<undefined | OrderDto>(() => store.getters['shop/order']);
const isEduMode = computed<boolean>(
  () => store.getters['adminEdu/isEduMode'] || 'edu' in router.currentRoute.value.query,
);
const cloudTabName = computed<string>(() => (isEduMode.value ? 'Licenses' : 'Cloud'));
const usersTabName = computed<string>(() => (isEduMode.value ? 'EDU Users' : 'Users'));
const isEducational = computed<boolean>(() => store.getters['login/isEducational']);
// const isTeacher = computed<boolean>(() => store.getters['login/isTeacher']);
const isPurchaseAvailableForOrganization = computed<boolean>(
  () => store.getters['organizations/isPurchaseAvailableForOrganization'],
);
const isSupportAvailableForOrganization = computed<boolean>(
  () => store.getters['organizations/isSupportAvailableForOrganization'],
);
const uploadsTab = computed<NavigationLink>(() => {
  let uploadsTab: NavigationLink = {
    tab: 'Uploads',
    dataCy: 'nav-link-uploads',
    roles: ['admin'],
  };
  if (isEduMode.value) {
    uploadsTab = { ...uploadsTab, route: '/uploads' };
  } else {
    uploadsTab = {
      ...uploadsTab,
      subMenus: [
        {
          tab: 'Releases',
          route: '/uploads',
          dataCy: 'nav-link-releases',
        },
        {
          tab: 'Demos',
          route: '/admin-demos',
          dataCy: 'nav-link-admin-demos',
        },
      ],
    };
  }
  return uploadsTab;
});

const navigationLinks = computed<NavigationLink[]>(() =>
  [
    {
      tab: 'Products',
      route: '/products',
      dataCy: 'link-products-top',
      hidden: !phoneValidated.value,
    },
    {
      tab: 'Downloads',
      route: '/downloads',
      dataCy: 'link-downloads-top',
      hidden: !phoneValidated.value,
    },
    {
      tab: 'FREE TRIAL',
      route: '/free-trial',
      dataCy: 'link-free-trial-top',
      roles: ['guest', 'owner'],
      hidden: !phoneValidated.value || isEducational.value,
    },
    {
      tab: 'Viewer',
      dataCy: 'link-viewer',
      route: '/viewer',
      roles: ['guest', 'owner', 'member'],
      hidden: !phoneValidated.value,
    },
    // {
    //   tab: 'Demos',
    //   dataCy: 'link-demos',
    //   subMenus: demoSubmenus.value,
    //   roles: ['guest', 'owner', 'member'],
    //   hidden: !phoneValidated.value,
    // },
    {
      tab: 'Store',
      route: '/shop',
      dataCy: 'link-store',
      hidden: !phoneValidated.value || !isPurchaseAvailableForOrganization.value,
    },
    {
      tab: 'Organization',
      route: '/organization',
      dataCy: 'link-organization',
      hidden: !phoneValidated.value || isEducational.value,
    },
    {
      tab: 'People',
      route: '/people',
      dataCy: 'link-people-top',
      hidden: !phoneValidated.value || isEducational.value,
    },
    {
      tab: 'Students',
      route: '/students',
      dataCy: 'link-students-top',
      // hidden: !phoneValidated.value || !isEducational.value || !isTeacher.value,
      hidden: true,
    },
    {
      tab: 'Invoices',
      route: '/invoices',
      dataCy: 'link-invoices-top',
      hidden: !phoneValidated.value,
    },
    {
      tab: 'Dongles',
      route: '/dongles',
      dataCy: 'nav-link-dongles',
      hidden: isEduMode.value,
    },
    {
      tab: cloudTabName.value,
      route: '/cloud-products',
      dataCy: 'nav-link-cloud',
      hidden: false,
    },
    {
      tab: usersTabName.value,
      route: '/users',
      dataCy: 'nav-link-users',
    },
    {
      tab: 'Organizations',
      route: '/organizations',
      dataCy: 'nav-link-organizations',
      hidden: isEduMode.value,
    },
    uploadsTab.value,
    {
      tab: 'Discounts',
      route: '/discounts',
      dataCy: 'nav-link-discounts',
      hidden: isEduMode.value,
    },
    {
      tab: 'Staff',
      route: '/staff',
      dataCy: 'nav-link-staff',
      hidden: isEduMode.value,
    },
    {
      tab: 'Orders',
      route: '/orders',
      dataCy: 'nav-link-orders',
    },
    {
      tab: 'Inventory',
      route: '/inventory',
      dataCy: 'nav-link-inventory',
      hidden: isEduMode.value,
    },
    {
      tab: 'Pending Applicants',
      route: '/pending-applicants',
      dataCy: 'nav-link-pending-applicants',
      hidden: true, //!isEduMode.value,
    },
    {
      tab: 'Schools',
      route: '/schools',
      dataCy: 'nav-link-schools',
      hidden: !isEduMode.value,
    },
    {
      tab: 'Other',
      roles: ['admin'],
      subMenus: otherSubmenus.value,
      hidden: isEduMode.value,
    },
    {
      tab: 'Support',
      route: '/support-request',
      dataCy: 'nav-link-support',
      hidden: !isSupportAvailableForOrganization.value,
    },
  ].filter(link => !link.hidden),
);
const user = computed<undefined | UserDto>(() => store.getters['login/user']);
const isAdmin = computed<boolean>(() => store.getters['login/isAdmin']);
const userRoleName = computed<undefined | string>(() => store.getters['login/roleName']);
const isNotXs = computed<boolean>(() => !display.xs.value);
const appBarHeight = computed<number>(() => (display.smAndDown.value ? 56 : 64));
const loggedUserEduFields = computed(() => store.getters['login/loggedUserEduFields']);
const userNavigationLinks = computed<NavigationLink[]>(() => {
  return navigationLinks.value.filter(link => {
    const role = isAdmin.value ? 'admin' : userRoleName?.value || '';
    if (!link.route && link.subMenus && link.roles) {
      return link.roles.includes(role);
    }
    if (link.route) {
      const route = routes.find(r => r.path === link.route);
      if (!route) {
        throw new Error('Can not find route for navigation link');
      }
      return (route as unknown as MoaRouterConfig).meta.roles.includes(role);
    }
    throw new Error('Can not find route for navigation link');
  });
});
const profileLinks = computed<ProfileLink[]>(() => {
  const links: ProfileLink[] = [];
  if (store.getters['login/loggedIn'] && phoneValidated.value) {
    links.push({ tab: 'Profile', route: '/profile', dataCy: 'profile-link-top' });
  }
  links.push({ tab: 'Legal', dataCy: 'legal-link-top' });
  // store.getters['login/accessToken'] in case when user just pass through first step of EDU registration
  if (store.getters['login/loggedIn'] || loggedUserEduFields.value || store.getters['login/accessToken']) {
    // if (store.getters['login/loggedIn'] || loggedUserEduFields.value) {
    links.push({
      tab: loggedUserEduFields.value ? 'Postpone Registration' : 'Sign Out',
      route: '/auth/signout',
      dataCy: 'logout-link-top',
    });
  } else {
    links.push({ tab: 'Sign In', route: '/signin', dataCy: 'login-link-top' });
  }
  return links;
});
const cartProductsCount = computed<number>(() => store.getters['shop/cartProductsCount']);

const goToCart = async (): Promise<void> => {
  await router.push({ name: 'shop-cart' });
};
const switchEduMode = (): void => {
  store.dispatch('adminEdu/switchEduMode');
};
onMounted(() => {
  document.body.classList.remove('disable-scrolling');
});
</script>
